import { sdk } from '@needful-things-mvp/sdk';

import { hoursSince } from './api-format';
import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import { type Paginated, delay, paginate } from './mock';

export interface Vote {
  id: string;
  project: string;
  projectSlug: string;
  proposal: string;
  proposalSlug: string;
  artist: string;
  artistSlug: string;
  visitorId: string;
  date: string;
  updatedHours: number;
}

export interface ListVotesParams {
  search?: string;
  project?: string;
  page?: number;
  pageSize?: number;
}

export type PaginatedVotes = Paginated<Vote>;

export const VOTE_PAGE_SIZE = 12;
export const VOTE_PAGE_SIZE_OPTIONS = [12, 24, 48, 96];

const SEED = [
  {
    project: 'Bloom',
    projectSlug: 'bloom',
    proposal: 'Still Water',
    proposalSlug: 'still-water',
    artist: 'Mara Oliveira',
    artistSlug: 'mara-oliveira',
  },
  {
    project: 'Bloom',
    projectSlug: 'bloom',
    proposal: 'Quarry Light',
    proposalSlug: 'quarry-light',
    artist: 'Tomas Beck',
    artistSlug: 'tomas-beck',
  },
  {
    project: 'Bloom',
    projectSlug: 'bloom',
    proposal: 'Paper Arches',
    proposalSlug: 'paper-arches',
    artist: 'Priya Nair',
    artistSlug: 'priya-nair',
  },
  {
    project: 'Salt & Iron',
    projectSlug: 'salt-and-iron',
    proposal: 'Salt Lines',
    proposalSlug: 'salt-lines',
    artist: 'Ade Bakare',
    artistSlug: 'ade-bakare',
  },
  {
    project: 'Salt & Iron',
    projectSlug: 'salt-and-iron',
    proposal: 'Anvil',
    proposalSlug: 'anvil',
    artist: 'Jonas Feld',
    artistSlug: 'jonas-feld',
  },
  {
    project: 'Ember Field',
    projectSlug: 'ember-field',
    proposal: 'Ember Study',
    proposalSlug: 'ember-study',
    artist: 'Lena Costa',
    artistSlug: 'lena-costa',
  },
];

function formatDate(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const db: Vote[] = Array.from({ length: 32 }, (_, index) => {
  const seed = SEED[index % SEED.length] as (typeof SEED)[number];
  return {
    id: `vote-${index + 1}`,
    ...seed,
    visitorId: `visitor-${(1024 + index * 37).toString(16)}`,
    date: formatDate(index),
    updatedHours: index * 3,
  };
});

/** Wire shape from `GET /admin/votes`. */
interface VoteDto {
  id: string;
  project: string;
  projectSlug: string;
  proposal: string;
  proposalSlug: string;
  artist: string;
  artistSlug: string;
  visitorId: string;
  createdAt: string;
}

function mapVote(dto: VoteDto): Vote {
  return {
    id: dto.id,
    project: dto.project,
    projectSlug: dto.projectSlug,
    proposal: dto.proposal,
    proposalSlug: dto.proposalSlug,
    artist: dto.artist,
    artistSlug: dto.artistSlug,
    visitorId: dto.visitorId,
    date: new Date(dto.createdAt).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    updatedHours: hoursSince(dto.createdAt),
  };
}

export async function listVotes(params: ListVotesParams = {}): Promise<PaginatedVotes> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.votes.get({
      query: {
        projectId: params.project,
        search: params.search,
        page: params.page,
        pageSize: params.pageSize,
      },
    });
    const page = unwrap<Paginated<VoteDto>>(response);
    return { ...page, items: page.items.map(mapVote) };
  }

  await delay();
  const { search, project, page, pageSize = VOTE_PAGE_SIZE } = params;

  const term = search?.trim().toLowerCase();
  const filtered = db.filter((vote) => {
    const matchesProject = !project || vote.projectSlug === project;
    const matchesSearch =
      !term ||
      vote.project.toLowerCase().includes(term) ||
      vote.proposal.toLowerCase().includes(term) ||
      vote.artist.toLowerCase().includes(term) ||
      vote.visitorId.toLowerCase().includes(term);
    return matchesProject && matchesSearch;
  });

  return paginate(filtered, page, pageSize);
}

export function queryVotes(params: ListVotesParams = {}): Promise<PaginatedVotes> {
  return listVotes(params);
}
